/**
 * Rich text parser for UserDataField values.
 * Supports <b>bold</b> and <size:+N>size offset</size> tags.
 * Tags can be nested: <b><size:+4>text</size></b>
 */

export interface TextStyle {
  bold: boolean;
  sizeOffset: number;
}

export interface TextSegment {
  text: string;
  style: TextStyle;
}

// Regex to detect any rich text tags
const RICH_TEXT_TAG_REGEX = /<\/?b>|<size:[+-]\d+>|<\/size>/;

// Tokenizer regex: splits input into tag tokens and text tokens
const TOKEN_REGEX = /(<b>|<\/b>|<size:[+-]\d+>|<\/size>)/;

/**
 * Fast check for whether a string contains any rich text tags.
 * Use as a guard clause to avoid parsing overhead for plain text.
 */
export function containsRichTextTags(text: string): boolean {
  if (!text) return false;
  return RICH_TEXT_TAG_REGEX.test(text);
}

/**
 * Parses a rich text string into styled segments.
 * Maintains a style stack to handle nesting.
 * Unclosed or malformed tags are treated as literal text.
 */
export function parseRichText(raw: string): TextSegment[] {
  if (!raw) return [];

  // Fast path: no tags at all
  if (!containsRichTextTags(raw)) {
    return [{ text: raw, style: { bold: false, sizeOffset: 0 } }];
  }

  const tokens = raw.split(TOKEN_REGEX);
  const segments: TextSegment[] = [];

  // Style stack: each entry represents a style modifier
  let bold = false;
  let sizeOffset = 0;
  const styleStack: Array<{ type: 'b' | 'size'; previousBold?: boolean; previousSizeOffset?: number }> = [];

  for (const token of tokens) {
    if (!token) continue;

    if (token === '<b>') {
      styleStack.push({ type: 'b', previousBold: bold });
      bold = true;
    } else if (token === '</b>') {
      // Find matching <b> on the stack
      let found = false;
      for (let i = styleStack.length - 1; i >= 0; i--) {
        if (styleStack[i].type === 'b') {
          bold = styleStack[i].previousBold!;
          styleStack.splice(i, 1);
          found = true;
          break;
        }
      }
      if (!found) {
        // No matching open tag, treat as literal text
        segments.push({ text: token, style: { bold, sizeOffset } });
      }
    } else if (token.startsWith('<size:')) {
      const match = /^<size:([+-]\d+)>$/.exec(token);
      if (match) {
        styleStack.push({ type: 'size', previousSizeOffset: sizeOffset });
        sizeOffset = parseInt(match[1], 10);
      } else {
        // Malformed, treat as literal text
        segments.push({ text: token, style: { bold, sizeOffset } });
      }
    } else if (token === '</size>') {
      // Find matching <size> on the stack
      let found = false;
      for (let i = styleStack.length - 1; i >= 0; i--) {
        if (styleStack[i].type === 'size') {
          sizeOffset = styleStack[i].previousSizeOffset!;
          styleStack.splice(i, 1);
          found = true;
          break;
        }
      }
      if (!found) {
        // No matching open tag, treat as literal text
        segments.push({ text: token, style: { bold, sizeOffset } });
      }
    } else {
      // Plain text token
      if (token.length > 0) {
        segments.push({ text: token, style: { bold, sizeOffset } });
      }
    }
  }

  // Merge adjacent segments with identical styles
  const merged: TextSegment[] = [];
  for (const seg of segments) {
    if (seg.text.length === 0) continue;
    const last = merged[merged.length - 1];
    if (last && last.style.bold === seg.style.bold && last.style.sizeOffset === seg.style.sizeOffset) {
      last.text += seg.text;
    } else {
      merged.push({ text: seg.text, style: { ...seg.style } });
    }
  }

  return merged;
}

/**
 * Strips all rich text tags from a string, returning plain text.
 */
export function stripRichTextTags(raw: string): string {
  if (!raw) return '';
  return raw.replace(/<\/?b>|<size:[+-]\d+>|<\/size>/g, '');
}
